import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang, whatsappLink } from "../i18n";
import { WhatsAppIcon } from "./WhatsAppButton";

export default function Navbar() {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/prodotti", label: t.nav_products, testid: "nav-link-products" },
    { to: "/projects", label: t.nav_projects, testid: "nav-link-projects" },
    { to: "/about", label: t.nav_about, testid: "nav-link-about" },
    { to: "/contact", label: t.nav_contact, testid: "nav-link-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? "glass-panel" : "bg-transparent"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="font-serif text-xl sm:text-2xl tracking-wide text-[#F3EFE6]">
          Arte <span className="text-[#C8A86B] italic">&</span> Infissi
        </Link>

        <nav className="hidden md:flex items-center gap-6 lg:gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `link-line text-xs lg:text-sm tracking-[0.15em] uppercase transition-colors duration-300 ${
                  isActive ? "text-[#C8A86B]" : "text-[#A39E93] hover:text-[#F3EFE6]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-quote-cta"
            className="inline-flex items-center gap-2 rounded-full border border-[#C8A86B]/60 px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-[#C8A86B] transition-all duration-300 hover:bg-[#C8A86B] hover:text-[#080808]"
          >
            <WhatsAppIcon size={13} /> {t.nav_quote}
          </a>
          <div className="flex items-center gap-1 text-xs font-mono border border-[#262420] px-1 py-1">
            <button
              data-testid="lang-switch-it"
              onClick={() => setLang("it")}
              className={`px-2 py-0.5 transition-colors duration-300 ${lang === "it" ? "bg-[#C8A86B] text-[#080808]" : "text-[#827D72] hover:text-[#F3EFE6]"}`}
            >
              IT
            </button>
            <button
              data-testid="lang-switch-en"
              onClick={() => setLang("en")}
              className={`px-2 py-0.5 transition-colors duration-300 ${lang === "en" ? "bg-[#C8A86B] text-[#080808]" : "text-[#827D72] hover:text-[#F3EFE6]"}`}
            >
              EN
            </button>
          </div>
        </nav>

        <button
          data-testid="mobile-menu-button"
          className="md:hidden text-[#F3EFE6]"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35 }}
            className="md:hidden glass-panel overflow-hidden"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <NavLink key={l.to} to={l.to} data-testid={`mobile-${l.testid}`} className="text-lg font-serif text-[#F3EFE6]">
                  {l.label}
                </NavLink>
              ))}
              <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" data-testid="mobile-nav-quote-cta" className="inline-flex w-fit items-center gap-2 bg-[#C8A86B] px-5 py-2.5 text-xs uppercase tracking-[0.15em] text-[#080808]">
                <WhatsAppIcon size={14} /> {t.nav_quote}
              </a>
              <div className="flex gap-2 text-xs font-mono pt-2">
                <button data-testid="mobile-lang-switch-it" onClick={() => setLang("it")} className={`px-3 py-1 border ${lang === "it" ? "border-[#C8A86B] text-[#C8A86B]" : "border-[#262420] text-[#827D72]"}`}>IT</button>
                <button data-testid="mobile-lang-switch-en" onClick={() => setLang("en")} className={`px-3 py-1 border ${lang === "en" ? "border-[#C8A86B] text-[#C8A86B]" : "border-[#262420] text-[#827D72]"}`}>EN</button>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
